'use client'

import { CommsBannerCarousel as CommsBannerCarouselComponent } from '@fukku/comms-banners/CommsBannerCarousel/CommsBannerCarousel'
import type { CommElement } from '@fukku/comms-banners/types'
import { useViewPromotion } from 'analytics/hooks/useViewPromotion/useViewPromotion'
import type { Promotion } from 'analytics/types/EEC'
import { useCallback, useEffect, useRef, useState } from 'react'
import type {
	CommBanner,
	CommCtaShop,
	SectionType,
} from '../../../../Home.type'
import { commsToCommsWithLinksAndPromotions } from './commsToCommsWithLinksAndPromotions'

interface CommsCarouselClientProps {
	readonly sectionType: SectionType.CommsCarousel
	readonly slot: string
	readonly sectionUid: string
	readonly previousButtonLabel: string
	readonly nextButtonLabel: string
	readonly infoIconLabel: string
	readonly closeButtonLabel: string
	readonly allCommsBrandCtas: CommCtaShop[]
	readonly allCommsSectionCtas: CommCtaShop[]
	readonly comms: CommBanner[]
}

export interface CurrentVisibleComm {
	id: string
	commRef: React.RefObject<HTMLDivElement> | null
}

export function CommsCarouselClient({
	sectionType,
	slot,
	sectionUid,
	previousButtonLabel,
	nextButtonLabel,
	infoIconLabel,
	closeButtonLabel,
	allCommsBrandCtas,
	allCommsSectionCtas,
	comms,
}: CommsCarouselClientProps) {
	const carouselRef = useRef<HTMLDivElement>(null)
	const [currentVisibleCommId, setCurrentVisibleCommId] = useState<string>(
		comms[0].uid
	)

	const handleOnShowComm = useCallback(
		({ id }: CommElement) => {
			setCurrentVisibleCommId(id)
		},
		[currentVisibleCommId]
	)

	const { commsPromotions, allCommsWithLinks } =
		commsToCommsWithLinksAndPromotions({
			comms,
			allCommsBrandCtas,
			allCommsSectionCtas,
			infoIconLabel,
			closeButtonLabel,
			sectionType,
			slot,
			handleOnShowComm,
		})

	const filteredPromotions = commsPromotions.find(
		({ id }) => id === currentVisibleCommId
	)?.promotions as Promotion[]

	useViewPromotion({
		ref: carouselRef,
		promotions: filteredPromotions,
		elementKey: currentVisibleCommId,
	})

	const handleResize = () => {
		const carouselHeight =
			carouselRef.current?.getBoundingClientRect().height ?? 0

		if (carouselHeight === 0 || slot !== '0') {
			return
		}

		const headerHeight = getComputedStyle(
			document.documentElement
		).getPropertyValue('--header-height')

		const newHeight = Math.round(
			Number.parseFloat(headerHeight) * 16 + carouselHeight
		)
		const bodyElement = carouselRef.current?.ownerDocument.body
		bodyElement?.style.setProperty('--reduce-height', `${newHeight}px`)
	}

	useEffect(() => {
		if (carouselRef.current) {
			handleResize()

			const resizeObserver = new ResizeObserver(handleResize)
			resizeObserver.observe(carouselRef.current)

			return () => {
				resizeObserver.disconnect()
			}
		}
		return undefined
	}, [])

	if (allCommsWithLinks.length === 0) {
		return null
	}

	return (
		<div ref={carouselRef}>
			<CommsBannerCarouselComponent
				id={sectionUid}
				previousButtonLabel={previousButtonLabel}
				nextButtonLabel={nextButtonLabel}
				comms={allCommsWithLinks}
				data-testid='landings.home.commsBannerCarousel'
			/>
		</div>
	)
}
